import React, { useState } from 'react';

import { HoverCard, HoverCardContent, HoverCardTrigger } from 'components/ui/hover-card';
import { ChevronDownIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { CHECK_WITH_YOU_URL, CONTRACT_ACCOMPANIMENT_URL, MEMBERSHIP_URL } from 'shared/constants';
import { useRouteChangeHandler } from 'shared/hooks';
import { IframeDialog } from 'shared/ui/IframeDialog';

import {
  BrowseIcon,
  CareerIcon,
  ContractSupportIcon,
  FullyGuidedIcon,
  MembershipIcon,
  RequestRoomIcon,
  RightSideIcon,
} from './Icons';

const ServicesMenu = () => {
  const { t, i18n } = useTranslation('common');
  const columnWidth = i18n.language === 'vi' ? 'w-[324px]' : 'w-[294px]';
  const { handleRouteChange } = useRouteChangeHandler();
  const [isOpen, setIsOpen] = useState(false);

  const handleMenuClick = (path: string, menu: string) => {
    window.dataLayer?.push({
      event: 'click_menu',
      menu,
      section: 'Services',
    });

    if (path) {
      handleRouteChange(path);
    }
  };

  const handleTriggerClick = () => {
    setIsOpen(true);
  };

  return (
    <li tabIndex={0}>
      <HoverCard open={isOpen} onOpenChange={setIsOpen} openDelay={0} closeDelay={100}>
        <HoverCardTrigger
          className="flex cursor-pointer items-center gap-1 rounded-lg px-3 py-[6px] text-b3 font-semibold hover:bg-[#F6F6F6]"
          onClick={handleTriggerClick}
        >
          <span>{t('navigation.services')}</span>
          <ChevronDownIcon className="size-4" />
        </HoverCardTrigger>

        <HoverCardContent
          align="start"
          alignOffset={-300}
          sideOffset={10}
          // max-w 클램프: 고정폭 컬럼 합(EN ~1303px, VI ~1393px)이 뷰포트를 넘으면 fixed 포탈이라
          // 스크롤로도 접근 불가 — 컬럼들이 shrink 되도록 상한을 뷰포트에 묶는다
          className="ml-4 w-full max-w-[calc(100vw-24px)] rounded-lg border-none bg-white p-0 shadow-[0px_3px_12.1px_0px_rgba(0,0,0,0.14)]"
        >
          <div className="flex">
            {/* Main content area */}
            <div className="flex flex-1 gap-[30px] px-9 py-8">
              {/* Online section */}
              <div className={`flex ${columnWidth} flex-col gap-3`}>
                <div className="px-3">
                  <p className="text-c5 font-medium text-primary">{t('servicesMenu.sections.online')}</p>
                </div>

                <div className="flex flex-col gap-2">
                  {/* Room Search */}
                  <a
                    href="/listing"
                    className="flex cursor-pointer flex-col gap-1.5 rounded-md p-3 hover:bg-[#F8F8F8]"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuClick('/listing', 'Room Search');
                    }}
                  >
                    <div className="flex items-center gap-[5px]">
                      <BrowseIcon />
                      <span className="overflow-hidden text-ellipsis text-[13px] font-medium not-italic leading-[150%] tracking-[-0.286px] text-[#A9A9A9]">
                        {t('servicesMenu.roomSearch.subtitle')}
                      </span>
                    </div>
                    <h3 className="text-b5 font-medium">{t('servicesMenu.roomSearch.title')}</h3>
                    <p className="overflow-hidden text-ellipsis text-[13px] font-normal leading-[1.4] tracking-[-0.286px] text-[#8E8E8E]">
                      <Trans i18nKey="servicesMenu.roomSearch.description" />
                    </p>
                  </a>

                  {/* Request a Room */}
                  <a
                    href="/request-room"
                    className="flex cursor-pointer flex-col gap-1.5 rounded-md p-3 hover:bg-[#F8F8F8]"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuClick('/request-room', 'Request a Room');
                    }}
                  >
                    <div className="flex items-center gap-[5px]">
                      <RequestRoomIcon />
                      <span className="overflow-hidden text-ellipsis text-[13px] font-medium not-italic leading-[150%] tracking-[-0.286px] text-[#A9A9A9]">
                        {t('servicesMenu.requestRoom.subtitle')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-b5 font-medium">{t('servicesMenu.requestRoom.title')}</h3>
                    </div>
                    <p className="overflow-hidden text-ellipsis text-[13px] font-normal leading-[1.4] tracking-[-0.286px] text-[#8E8E8E]">
                      <Trans i18nKey="servicesMenu.requestRoom.description" />
                    </p>
                  </a>
                </div>
              </div>

              {/* Offline section */}
              <div className={`flex ${columnWidth} flex-col gap-3`}>
                <div className="px-3">
                  <p className="text-c5 font-medium text-primary">{t('servicesMenu.sections.offline')}</p>
                </div>

                <div className="flex flex-col gap-2">
                  {/* Check With You */}
                  <IframeDialog title="Check With You" src={CHECK_WITH_YOU_URL}>
                    <div
                      className="flex cursor-pointer flex-col gap-1.5 rounded-md p-3 hover:bg-[#F8F8F8]"
                      onClick={() => {
                        handleMenuClick('', 'Check With You');
                      }}
                    >
                      <div className="flex items-center gap-[5px]">
                        <FullyGuidedIcon />
                        <span className="overflow-hidden text-ellipsis text-[13px] font-medium not-italic leading-[150%] tracking-[-0.286px] text-[#A9A9A9]">
                          {t('servicesMenu.checkWithYou.subtitle')}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-b5 font-medium">{t('servicesMenu.checkWithYou.title')}</h3>
                        <span className="whitespace-nowrap rounded bg-[#FFF2E5] px-1.5 py-0.5 text-[11px] font-medium leading-[1.5] tracking-[-0.242px] text-[#FF7A00]">
                          {t('servicesMenu.checkWithYou.badge')}
                        </span>
                      </div>
                      <p className="overflow-hidden text-ellipsis text-[13px] font-normal leading-[1.4] tracking-[-0.286px] text-[#8E8E8E]">
                        <Trans i18nKey="servicesMenu.checkWithYou.description" />
                      </p>
                    </div>
                  </IframeDialog>

                  {/* Contract Accompaniment */}
                  <IframeDialog title="Contract Accompaniment" src={CONTRACT_ACCOMPANIMENT_URL}>
                    <div
                      className="flex cursor-pointer flex-col gap-1.5 rounded-md p-3 hover:bg-[#F8F8F8]"
                      onClick={() => {
                        handleMenuClick('', 'Contract Accompaniment');
                      }}
                    >
                      <div className="flex items-center gap-[5px]">
                        <ContractSupportIcon />
                        <span className="overflow-hidden text-ellipsis text-[13px] font-medium not-italic leading-[150%] tracking-[-0.286px] text-[#A9A9A9]">
                          {t('servicesMenu.contractAccompaniment.subtitle')}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-b5 font-medium">{t('servicesMenu.contractAccompaniment.title')}</h3>
                        <span className="whitespace-nowrap rounded bg-[#FFF1F1] px-1.5 py-0.5 text-[11px] font-medium leading-[1.5] tracking-[-0.242px] text-primary">
                          {t('servicesMenu.contractAccompaniment.badge')}
                        </span>
                      </div>
                      <p className="overflow-hidden text-ellipsis text-[13px] font-normal leading-[1.4] tracking-[-0.286px] text-[#8E8E8E]">
                        <Trans i18nKey="servicesMenu.contractAccompaniment.description" />
                      </p>
                    </div>
                  </IframeDialog>
                </div>
              </div>

              {/* Divider */}
              <div className="h-auto w-px bg-[#EAEAEA]" />

              {/* Ongoing section */}
              <div className={`flex ${columnWidth} flex-col gap-3`}>
                <div className="px-3">
                  <p className="text-c5 font-medium text-primary">{t('servicesMenu.sections.ongoing')}</p>
                </div>

                <div className="flex flex-col gap-2">
                  {/* Membership */}
                  <a
                    href={MEMBERSHIP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex cursor-pointer flex-col gap-1.5 rounded-md p-3 hover:bg-[#F8F8F8]"
                    onClick={() => handleMenuClick('', 'Membership')}
                  >
                    <div className="flex items-center gap-[5px]">
                      <MembershipIcon />
                      <span className="overflow-hidden text-ellipsis text-[13px] font-medium not-italic leading-[150%] tracking-[-0.286px] text-[#A9A9A9]">
                        {t('servicesMenu.membership.subtitle')}
                      </span>
                    </div>
                    <h3 className="text-b5 font-medium">{t('servicesMenu.membership.title')}</h3>
                    <p className="overflow-hidden text-ellipsis text-[13px] font-normal leading-[1.4] tracking-[-0.286px] text-[#8E8E8E]">
                      <Trans i18nKey="servicesMenu.membership.description" />
                    </p>
                  </a>

                  {/* Jobs */}
                  <a
                    href="/jobs"
                    className="flex cursor-pointer flex-col gap-1.5 rounded-md p-3 hover:bg-[#F8F8F8]"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuClick('/jobs', 'Jobs');
                    }}
                  >
                    <div className="flex items-center gap-[5px]">
                      <CareerIcon />
                      <span className="overflow-hidden text-ellipsis text-[13px] font-medium not-italic leading-[150%] tracking-[-0.286px] text-[#A9A9A9]">
                        {t('servicesMenu.jobs.subtitle')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-b5 font-medium">{t('servicesMenu.jobs.title')}</h3>
                    </div>
                    <p className="overflow-hidden text-ellipsis text-[13px] font-normal leading-[1.4] tracking-[-0.286px] text-[#8E8E8E]">
                      <Trans i18nKey="servicesMenu.jobs.description" />
                    </p>
                  </a>
                </div>
              </div>
            </div>

            {/* Right side - CTA section */}
            <div className="flex w-[288px] flex-col rounded-br-lg rounded-tr-lg bg-[#FBFBFB] px-9 pb-11 pt-8">
              <div className="pb-[25px]">
                <RightSideIcon />
              </div>

              <div className="flex flex-1 flex-col justify-between">
                <div>
                  <p className="text-b5 font-medium leading-[1.5] text-[#222]">
                    <Trans i18nKey="servicesMenu.video.title" />
                  </p>
                </div>

                <div>
                  <p className="mb-[14px] text-[13px] font-normal leading-[1.4] tracking-[-0.286px] text-[#8E8E8E]">
                    <Trans i18nKey="servicesMenu.video.description" />
                  </p>

                  <a
                    href="/all-services"
                    className="text-[13px] font-normal leading-[1.4] tracking-[-0.286px] text-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMenuClick('/all-services', 'Find your service');
                    }}
                  >
                    <Trans i18nKey="servicesMenu.video.cta" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    </li>
  );
};

export default ServicesMenu;
